/**
 * Nuendo のトラックアーカイブ XML（File > Export > Track Archive）の解析。
 *
 * MA 自動化スクリプト（ma_automation.py）の NuendoXMLParser を移植したもの。
 * 取得対象はビデオトラックのイベントとマーカートラックのマーカー。
 * ビデオ / マーカートラックの時刻は秒（Domain の Period = 1）で書かれている。
 */

export interface NuendoVideoEvent {
  /** クリップ名（PVideoClip の Name。Nuendo のインフォラインで変更できる名前） */
  name: string;
  /** ソースファイル名（FNPath の Name） */
  fileName: string;
  /** タイムライン上の開始位置（秒） */
  start: number;
  /** 長さ（秒） */
  length: number;
  /** ソースファイル内のイン点（秒） */
  offset: number;
}

export interface NuendoVideoTrack {
  name: string;
  events: NuendoVideoEvent[];
}

export interface NuendoMarker {
  name: string;
  /** 開始位置（秒） */
  start: number;
  /** 長さ（秒）。0 = 通常マーカー、正 = サイクルマーカー */
  length: number;
}

export interface NuendoMarkerTrack {
  name: string;
  markers: NuendoMarker[];
}

export interface NuendoArchive {
  videoTracks: NuendoVideoTrack[];
  markerTracks: NuendoMarkerTrack[];
}

interface VideoClipInfo {
  name: string;
  fileName: string;
}

const childElements = (
  parent: Element,
  tag: string,
  name?: string,
): Element[] =>
  Array.from(parent.children).filter(
    (child) =>
      child.tagName === tag &&
      (name === undefined || child.getAttribute("name") === name),
  );

const childValue = (
  parent: Element,
  tag: string,
  name: string,
): string | null =>
  childElements(parent, tag, name)[0]?.getAttribute("value") ?? null;

/** 直下の float 値。Nuendo は 0 の値（Offset 等）を省略するため、無ければ 0 */
const childFloat = (parent: Element, name: string): number => {
  const raw = childValue(parent, "float", name);
  const value = raw === null ? NaN : parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
};

/** トラック（M*TrackEvent）の表示名。MListNode 直下の Name */
const trackName = (track: Element, fallback: string): string => {
  const node = childElements(track, "obj").find(
    (c) => c.getAttribute("class") === "MListNode",
  );
  const name = node ? childValue(node, "string", "Name") : null;
  return name?.trim() || fallback;
};

const readVideoClip = (clip: Element): VideoClipInfo => {
  const path = clip.querySelector('obj[class="FNPath"]');
  return {
    name: childValue(clip, "string", "Name") ?? "",
    fileName: (path && childValue(path, "string", "Name")) ?? "",
  };
};

/**
 * XML 文字列を解析する。XML として読めない・トラックアーカイブでない場合は
 * Error を投げる（メッセージはそのまま画面に出す）。
 */
export function parseNuendoArchive(xmlText: string): NuendoArchive {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("XML として読み込めませんでした");
  }
  const root = doc.documentElement;
  if (root.tagName !== "tracklist2") {
    throw new Error("Nuendo のトラックアーカイブ XML ではありません");
  }

  // PVideoClip は 2 件目以降のイベントから ID 参照（<obj name="Node" ID=...>）
  // だけで指されることがあるため、先に ID で索引しておく
  const clipIndex = new Map<string, VideoClipInfo>();
  root.querySelectorAll('obj[class="PVideoClip"]').forEach((clip) => {
    const id = clip.getAttribute("ID");
    if (id) clipIndex.set(id, readVideoClip(clip));
  });

  const videoTracks: NuendoVideoTrack[] = [];
  root
    .querySelectorAll('obj[class="MVideoTrackEvent"]')
    .forEach((track, index) => {
      const events: NuendoVideoEvent[] = [];
      track.querySelectorAll('obj[class="MVideoEvent"]').forEach((event) => {
        const clipElement = event.querySelector('obj[class="PVideoClip"]');
        let clip: VideoClipInfo | undefined;
        if (clipElement) {
          clip = readVideoClip(clipElement);
        } else {
          const refId = childElements(event, "obj", "Node")[0]?.getAttribute(
            "ID",
          );
          clip = refId ? clipIndex.get(refId) : undefined;
        }
        if (!clip) return;
        events.push({
          name: clip.name,
          fileName: clip.fileName,
          start: childFloat(event, "Start"),
          length: childFloat(event, "Length"),
          offset: Math.max(0, childFloat(event, "Offset")),
        });
      });
      events.sort((a, b) => a.start - b.start);
      videoTracks.push({
        name: trackName(track, index === 0 ? "VIDEO" : `VIDEO ${index + 1}`),
        events,
      });
    });

  const markerTracks: NuendoMarkerTrack[] = [];
  root
    .querySelectorAll('obj[class="MMarkerTrackEvent"]')
    .forEach((track, index) => {
      const markers: NuendoMarker[] = [];
      track.querySelectorAll('list[name="Events"] > obj').forEach((event) => {
        markers.push({
          name: (childValue(event, "string", "Name") ?? "").trim(),
          start: childFloat(event, "Start"),
          length: Math.max(0, childFloat(event, "Length")),
        });
      });
      markers.sort((a, b) => a.start - b.start);
      markerTracks.push({
        name: trackName(track, `MARKER ${index + 1}`),
        markers,
      });
    });

  return { videoTracks, markerTracks };
}
